import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, signal, WritableSignal } from '@angular/core';
import { ButtonPadding } from '@app/components/button/button.component';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { PlIcon } from '@app/icon-library/icons';
import * as moment from 'moment';
import { Moment } from 'moment';
import { I18nService } from '@app/i18n';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';

export interface ModalAddToCalendarInputs {
  title: string;
  description: string;
  timeFormat: TimeFormat;
}

interface CalendarData {
  description: string;
  endDate: string;
  nowDate: string;
  startDate: string;
  offset: string;
  title: string;
}

@Component({
  selector: 'pl-modal-add-to-calendar',
  templateUrl: './modalAddToCalendar.component.html',
})
export class ModalAddToCalendarComponent {
  public title: string = '';
  public description: string = '';
  public timeFormat: WritableSignal<TimeFormat> = signal(TimeFormat.amPm);

  // This has to be more than the timepickerGap gap AND a multiple of the timepicker gap
  public minutesRounding: number = 30 as const;
  public timepickerGap: number = 30 as const;

  public dateSelected: string = moment().format(`YYYY-MM-DD`);
  public timeSelected: string = this.getTimeNowRoundedUpThirtyMinutes();

  public calendarIcons: PlIcon[] = this.getAllCalendarIcons();

  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalAddToCalendarInputs,
    private dialogRef: MatDialogRef<ModalAddToCalendarComponent>,
    private i18n: I18nService,
  ) {
    this.title = data.title;
    this.description = data.description;
    this.timeFormat.set(data.timeFormat);
  }

  public closeModal(): void {
    this.dialogRef.close();
  }

  public getTimeNowRoundedUpThirtyMinutes(): string {
    const now: Moment = moment();
    const remainder: number = this.minutesRounding - (now.minute() % this.minutesRounding);
    const timeUpdated: Moment = now.add(remainder, 'minutes');
    return timeUpdated.format('HH:mm');
  }

  public addEventToGoogleCalendar(calendarData: CalendarData): void {
    let googleCalendarUrl: string = `https://www.google.com/calendar/render?action=TEMPLATE`;
    googleCalendarUrl += `&text=${encodeURIComponent(this.title)}`;
    googleCalendarUrl += `&dates=${calendarData.startDate}/${calendarData.endDate}`;
    googleCalendarUrl += `&details=${encodeURIComponent(this.buildHtmlDescription(calendarData.description))}`;

    window.open(googleCalendarUrl, '_blank');
  }

  private buildHtmlDescription(description: string): string {
    let htmlDescription: string = '<div>';
    const descriptionTag: string = `<p>${description}</p>`;

    const linkTag: string = `<p>${this.i18n.translate(`blocks.lifeAct.view_lifeact`)}: <a href="${location.href}">${
      location.href
    }</a></p>`;

    htmlDescription += descriptionTag;
    htmlDescription += linkTag;
    htmlDescription += '</div>';
    return htmlDescription;
  }

  private getCalendarData(): CalendarData {
    const formatMoment: string = 'YYYYMMDDTHHmmss';
    const startDate: Moment = moment(`${this.dateSelected} ${this.timeSelected}`);

    return {
      description: this.description,
      endDate: startDate.clone().add(1, 'h').format(formatMoment),
      nowDate: moment().format(formatMoment),
      offset: startDate.format('+-HH:mm'),
      startDate: startDate.format(formatMoment),
      title: this.title,
    };
  }

  public addEventWithICSFile(calendarData: CalendarData): void {
    const icsData: string = this.formatIcsCalendarData(calendarData);
    const icsBlob: Blob = this.getIcsBlob(icsData);
    const fileName: string = this.getIcsFileName(calendarData.title);

    const url: string = window.URL.createObjectURL(icsBlob);

    const a: HTMLAnchorElement = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  public formatIcsCalendarData(calendarData: CalendarData): string {
    return [
      `BEGIN:VCALENDAR`,
      `VERSION:2.0`,
      `BEGIN:VEVENT`,
      `CLASS:PUBLIC`,
      `DESCRIPTION:${this.formatIcsText(this.buildDescriptionForIcsFile(calendarData.description), 0)}`,
      `DTSTART:${calendarData.startDate}`,
      `TZOFFSETFROM:${calendarData.offset}`,
      `DTEND:${calendarData.endDate}`,
      `SUMMARY:${this.formatIcsText(calendarData.title, 66)}`,
      `TRANSP:TRANSPARENT`,
      `END:VEVENT`,
      `END:VCALENDAR`,
      `UID:${this.getRandomId()}`,
      `DTSTAMP:${calendarData.nowDate}`,
      `PRODID:poentialife-program`,
    ].join('\n');
  }

  private getIcsBlob(icsData: string): Blob {
    return new Blob([icsData], {
      type: 'application/octet-stream',
    });
  }

  private getIcsFileName(title: string): string {
    if (!title) {
      return 'event.ics';
    }
    return `${title.replace(/[^\w ]+/g, '')}.ics`;
  }

  private getRandomId(): string {
    return Math.random().toString(36).substring(2);
  }

  private buildDescriptionForIcsFile(description: string): string {
    return `${description}\n\n${this.i18n.translate(`blocks.lifeAct.view_lifeact`)}: ${location.href}`;
  }

  private formatIcsText(text: string, maxLength: number): string {
    if (!text) {
      return '';
    }
    let icsText: string = text.replace(/\n/g, '\\n');

    if (maxLength) {
      icsText = icsText.substring(0, maxLength);
    }

    return icsText;
  }

  public addEventToCalendar(iconName: PlIcon): void {
    const calendarData: CalendarData = this.getCalendarData();
    if (iconName === PlIcon.google) {
      this.addEventToGoogleCalendar(calendarData);
    } else {
      this.addEventWithICSFile(calendarData);
    }

    this.closeModal();
  }

  private getAllCalendarIcons(): PlIcon[] {
    return [PlIcon.google, PlIcon.calendar_mac, PlIcon.calendar_outlook];
  }
}
